import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import type { Filter } from '../@types/ElasticSearchResponse';
import type { WaykeQueryLocation } from '../@types/WaykeLocation';
import QueryFilter, { queryFilterImport } from '../helpers/query-filter';
import { slugify } from '../helpers/urls';
import { getFacetList } from '../utils/reselectors';

// Searching for Göteborg will create /sok/goteborg.
// In order to match above we need to remove "", Sverige" from filter displaynames. Ex "Göteborg, Sverige"

const fixLocationFilterName = (displayName: string) => {
  return displayName.replace(/, (Sverige|Norge)/g, '');
};

const useQueryFilterFromUrl = (path = '/sok') => {
  const location = useLocation();

  const facets = useSelector(getFacetList);

  const params = new URLSearchParams(location.search);

  const qfPath = useMemo(() => {
    if (location.pathname.substring(0, path.length) !== path) return undefined;

    const queryURL = location.pathname.substring(path.length);

    const queryURLValues = queryURL
      .toLocaleLowerCase()
      .replace(/^\/|\/$/g, '')
      .split('/')
      .filter((q) => !!q);

    let queryFilter = new QueryFilter();
    facets.forEach((facet) =>
      facet?.filters?.forEach((filter) => {
        const fixedDisplayName = facet.isLocationFacet
          ? fixLocationFilterName(filter.displayName)
          : filter.displayName;
        const index = fixedDisplayName ? queryURLValues.indexOf(slugify(fixedDisplayName)) : -1;
        if (index > -1) {
          if (facet.isLocationFacet) {
            const _filter = filter as unknown as Filter & WaykeQueryLocation;
            queryFilter = queryFilter.addSpatial({
              ..._filter,
              radius: _filter.distance,
            });
          } else if (facet.isRangeFacet && facet.id === 'modelYear') {
            queryFilter = queryFilter.addRange(
              facet.id,
              Number.parseInt(filter.displayName, 10),
              Number.parseInt(filter.displayName, 10),
            );
          } else if (!facet.isRangeFacet) {
            queryFilter = queryFilter.add(facet.id, filter.displayName);
          }
          /** Removes matched queryUrlFacetValue
           *  Example: sok/mini will both match manufacturer MINI and modelSeries Mini causing zero results
           *  because no car has manufacture MINI and modelSeries Mini.
           *  ModelSeries mini is related to manufacturer Ausin (Old and before BMW bought Austin)
           **/
          queryURLValues.splice(index, 1);
        }
      }),
    );
    return queryFilter;
  }, [location.pathname, facets]);

  const qfQuery = useMemo(() => {
    return queryFilterImport(params.get('qf'));
  }, [location.search]);

  const result = qfPath?.toString() ? qfPath : qfQuery;
  return result;
};

export default useQueryFilterFromUrl;
